import {
  Button,
  DropdownSelect,
  EmptyState,
  FlexBox,
  SelectItem,
  Text,
} from '@dixa/lib-shared-habla';
import { useTranslationFetch } from 'articleEditor/hooks/useTranslationFetch';
import { articleCssCDN } from 'lib-knowledge-article';
import { serializeToHtml } from 'lib-knowledge-document-structure/serialize';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  LanguageId,
  PublicationStatus,
} from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import Page from 'lib-knowledge-utils/components/page/index';
import { SourceConversationsChip } from 'lib-knowledge-utils/components/sourceConversations/index';
import { useTypedParams } from 'lib-knowledge-utils/hooks/useTypedParams';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CurrentVersionDetails } from 'versionHistory/components/currentVersionDetails';
import { useVersion } from 'versionHistory/hooks/useVersionContent';
import { useVersions } from 'versionHistory/hooks/useVersions';
import { ArticleTranslationVersionRestoreModal } from 'versionHistory/modals/ArticleTranslationVersionRestoreModal';
import { formatDate } from 'versionHistory/utils';
import { computeDiffHtml } from 'versionHistory/utils/applyDiffMarkers';

import { AiAssistedBadge } from 'components/aiAssistedBadge';
import { useShowAiProvenance } from 'hooks/useShowAiProvenance';

import styles from './versionHistory.module.scss';

const VersionHistory = (): JSX.Element => {
  const { articleId } = useTypedParams(['articleId']);
  const { currentLanguage } = useContext(LanguageContext);
  const languageId = currentLanguage as LanguageId;
  const articleEditorUrl = routeGenerator.articleEdit({
    articleId,
    languageId: languageId ?? undefined,
  });
  const { showModal } = useModal();
  const showAiProvenance = useShowAiProvenance();
  const { versions, loading: versionsLoading } = useVersions({
    articleId,
    languageId,
  });

  const { data: currentVersion, loading: currentLoading } = useTranslationFetch(
    {
      articleId,
      languageId,
    },
  );
  const currentVersionPublicationStatus =
    currentVersion?.articleTranslation?.publicationStatus ??
    PublicationStatus.PublishedWithDraft;

  const [selectedVersionNumber, setVersionNumber] = useState<
    number | undefined
  >(undefined);
  const initialVersionIndex =
    currentVersionPublicationStatus === PublicationStatus.Published ? 1 : 0;
  const versionNumber =
    selectedVersionNumber ?? versions[initialVersionIndex]?.versionNumber;

  const { data: selectedVersion, loading: versionLoading } = useVersion({
    articleId,
    languageId,
    versionNumber,
  });

  const getHtmlWithTitle = useCallback(
    (html: string, title: string): string => {
      return `<h1 class="title">${title}</h1>${html}`;
    },
    [],
  );

  const { newMarked, oldMarked } = useMemo(() => {
    const currentArticle = currentVersion?.articleTranslation?.draft;
    const selectedArticle = selectedVersion?.article?.translation?.version;
    if (!currentArticle || !selectedArticle) {
      return { newMarked: '', oldMarked: '' };
    }
    const oldHtml = getHtmlWithTitle(
      serializeToHtml(selectedArticle.content, true),
      selectedArticle.title,
    );
    const newHtml = getHtmlWithTitle(
      serializeToHtml(currentArticle.content, true),
      currentArticle.title,
    );
    return computeDiffHtml(oldHtml, newHtml);
  }, [selectedVersion, currentVersion, getHtmlWithTitle]);

  const selectedMeta = versions.find((v) => v.versionNumber === versionNumber);
  const currentMeta = versions[0];

  const handleRestore = useCallback(() => {
    if (!selectedMeta) return;
    showModal(ArticleTranslationVersionRestoreModal, {
      versionMeta: selectedMeta,
      articleId,
      languageId,
      redirectToArticleEditorAfterRestore: true,
    });
  }, [showModal, selectedMeta, articleId, languageId]);

  if (versionsLoading) return <KnowledgeLoading />;

  if (
    versions.length === 0 ||
    (currentVersion?.articleTranslation?.publicationStatus ===
      PublicationStatus.Published &&
      versions.length === 1)
  ) {
    return (
      <Page>
        <Page.Head
          slotLeft={
            <Page.HeadBackLink
              backText="Back to editor"
              backProps={{ to: articleEditorUrl }}
            />
          }
          slotCenter={<Page.HeadTitle title="Version History" />}
        />
        <Page.Body className={styles.pageBody}>
          <EmptyState
            title={`No ${versions.length === 1 ? 'extra' : ''} versions available`}
            description={
              versions.length === 0
                ? 'The article was never published in this language'
                : 'The article was published once in this language, and there are no previous versions to show'
            }
          />
        </Page.Body>
      </Page>
    );
  }

  // If the current version is published, we don't need to show it in the select
  const versionItems = (
    currentVersion?.articleTranslation?.publicationStatus ===
    PublicationStatus.Published
      ? versions.slice(1)
      : versions
  ).map((v) => ({
    id: String(v.versionNumber),
    name:
      showAiProvenance && v.aiAssisted ? (
        <FlexBox alignItems="center" gap="100">
          {formatDate(v.publishedAt)}
          <AiAssistedBadge />
        </FlexBox>
      ) : (
        formatDate(v.publishedAt)
      ),
  }));

  const isLoading = versionLoading || currentLoading;

  return (
    <Page>
      <Page.Head
        slotLeft={
          <Page.HeadBackLink
            backText="Back to editor"
            backProps={{ to: articleEditorUrl }}
          />
        }
        slotCenter={<Page.HeadTitle title="Version History" />}
      />
      <Page.Body className={styles.pageBody}>
        <Page>
          <Page.Head
            className={styles.versionHeader}
            slotLeft={
              <CurrentVersionDetails
                publicationStatus={currentVersionPublicationStatus}
                versionMeta={currentMeta}
                editedAt={currentVersion?.articleTranslation?.draft.updatedAt}
                editedBy={
                  currentVersion?.articleTranslation?.draft.updatedBy?.name ??
                  undefined
                }
                isAiAssisted={
                  showAiProvenance &&
                  (currentVersionPublicationStatus ===
                  PublicationStatus.Published
                    ? !!currentMeta?.aiAssisted
                    : !!currentVersion?.articleTranslation?.draft.aiAssisted)
                }
              />
            }
            slotRight={
              <FlexBox
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                className={styles.previousVersionHeader}
              >
                <div>
                  <Text.Paragraph variant="1">
                    <strong>Previous Version</strong>
                  </Text.Paragraph>
                  <FlexBox alignItems="center" gap="100">
                    <DropdownSelect
                      items={versionItems}
                      itemComponent={SelectItem}
                      noArrow={false}
                      selectedItems={
                        versionNumber
                          ? [
                              {
                                id: String(versionNumber),
                                name: selectedMeta
                                  ? formatDate(selectedMeta.publishedAt)
                                  : '',
                              },
                            ]
                          : []
                      }
                      onSelect={(item) => setVersionNumber(Number(item.id))}
                    >
                      {selectedMeta ? (
                        <FlexBox alignItems="center" gap="100">
                          {formatDate(selectedMeta.publishedAt)}
                          {showAiProvenance && selectedMeta.aiAssisted && (
                            <AiAssistedBadge />
                          )}
                        </FlexBox>
                      ) : (
                        'Select version'
                      )}
                    </DropdownSelect>
                    {showAiProvenance && (
                      <SourceConversationsChip
                        conversations={selectedMeta?.referencedConversations}
                      />
                    )}
                  </FlexBox>

                  {selectedMeta && (
                    <Text.Paragraph variant="2" subdued>
                      {selectedMeta.publishedBy &&
                        `Published by ${selectedMeta.publishedBy}`}
                    </Text.Paragraph>
                  )}
                </div>
                <Button
                  variant="primary"
                  disabled={!versionNumber}
                  onClick={handleRestore}
                >
                  {'Restore this version'}
                </Button>
              </FlexBox>
            }
          />
          <Page.Body className={styles.pageBody}>
            <link
              rel="stylesheet"
              type="text/css"
              href={articleCssCDN({ noREMs: true })}
            />
            <div className={styles.diffOverflowContainer}>
              <FlexBox
                flexDirection="row"
                className={styles.diffContainer}
                alignItems="stretch"
              >
                <div className={`${styles.diffPanel} ${styles.diffPanelNew}`}>
                  {isLoading ? (
                    <KnowledgeLoading />
                  ) : (
                    <div
                      className="article"
                      dangerouslySetInnerHTML={{ __html: newMarked }}
                    />
                  )}
                </div>
                <div className={`${styles.diffPanel} ${styles.diffPanelOld}`}>
                  {isLoading ? (
                    <KnowledgeLoading />
                  ) : (
                    <div
                      className="article"
                      dangerouslySetInnerHTML={{ __html: oldMarked }}
                    />
                  )}
                </div>
              </FlexBox>
            </div>
          </Page.Body>
        </Page>
      </Page.Body>
    </Page>
  );
};

export default VersionHistory;
